import { create } from 'zustand';
import type { User } from '@skytech/shared';

import { api, setAccessToken, setRefreshHandler, ApiError } from '../../lib/api';

interface AuthResponse {
  user: User;
  accessToken: string;
}

type Status = 'idle' | 'loading' | 'authenticated' | 'guest';

interface AuthState {
  user: User | null;
  status: Status;
  /** Registered so other stores (cart) can run merge-on-login side effects. */
  onLogin: Array<(user: User) => void | Promise<void>>;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (input: {
    name: string;
    email: string;
    password: string;
    phone?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  registerLoginHook: (fn: (user: User) => void | Promise<void>) => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  status: 'idle',
  onLogin: [],

  registerLoginHook(fn) {
    set((s) => ({ onLogin: [...s.onLogin, fn] }));
  },

  /**
   * Restores a session on load. /auth/refresh reads the HttpOnly cookie; if it
   * succeeds we have a token and fetch the profile. Any failure means "guest".
   */
  async bootstrap() {
    set({ status: 'loading' });
    // The api client calls this on a 401 to transparently refresh.
    setRefreshHandler(async () => {
      try {
        const res = await api.post<{ accessToken: string }>('/auth/refresh');
        setAccessToken(res.data.accessToken);
        return res.data.accessToken;
      } catch {
        setAccessToken(null);
        set({ user: null, status: 'guest' });
        return null;
      }
    });

    try {
      const refresh = await api.post<{ accessToken: string }>('/auth/refresh');
      setAccessToken(refresh.data.accessToken);
      const me = await api.get<User>('/auth/me');
      set({ user: me.data, status: 'authenticated' });
    } catch {
      setAccessToken(null);
      set({ user: null, status: 'guest' });
    }
  },

  async login(email, password) {
    const res = await api.post<AuthResponse>('/auth/login', { email, password });
    setAccessToken(res.data.accessToken);
    set({ user: res.data.user, status: 'authenticated' });
    await Promise.all(get().onLogin.map((fn) => fn(res.data.user)));
  },

  async register(input) {
    try {
      const res = await api.post<AuthResponse>('/auth/register', input);
      setAccessToken(res.data.accessToken);
      set({ user: res.data.user, status: 'authenticated' });
      await Promise.all(get().onLogin.map((fn) => fn(res.data.user)));
    } catch (err) {
      if (err instanceof ApiError) throw err;
      throw new Error('Registration failed');
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      setAccessToken(null);
      set({ user: null, status: 'guest' });
    }
  },
}));
